import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useAlert } from '../context/AlertContext';
import {
  downloadBase64,
  ExportFormatKey,
  useExportLegalClient,
  useLegalClientExportFormats,
} from '../hooks/reactQuerys/ExportQueries';

interface IProps {
  idLegalClient: number;
  /** Wenn der Export-Button als ListItemButton oder Icon gerendert werden
   *  soll, kann hier ein Custom-Trigger reingegeben werden. */
  triggerLabel?: string;
  variant?: 'outlined' | 'contained' | 'text';
}

const FORMAT_OPTIONS: {
  value: ExportFormatKey;
  label: string;
}[] = [
  { value: 'dino', label: 'DiNo-Datensicherung' },
  { value: 'strukturdatensatz', label: 'Strukturdatensatz' },
  { value: 'xjustiz', label: 'XJustiz' },
];

export const LegalClientExport: React.FC<IProps> = ({
  idLegalClient,
  triggerLabel = 'Export',
  variant = 'outlined',
}) => {
  const { showAlert } = useAlert();
  const [isOpen, setIsOpen] = useState(false);
  const [format, setFormat] = useState<ExportFormatKey>('dino');
  const [xjustizVersion, setXjustizVersion] = useState<string | undefined>(
    undefined
  );
  const { exportLegalClient, isExporting } = useExportLegalClient();
  const { formats } = useLegalClientExportFormats();
  const xjustizFormat = formats.find(f => f.key === 'xjustiz');

  if (xjustizFormat?.defaultVersion && xjustizVersion === undefined) {
    setXjustizVersion(xjustizFormat.defaultVersion);
  }

  const handleExport = async () => {
    try {
      const response = await exportLegalClient({
        idLegalClient,
        format,
        version: format === 'xjustiz' ? xjustizVersion : undefined,
      });
      downloadBase64(response);
      setIsOpen(false);
    } catch (error) {
      showAlert({ severity: 'error', text: 'Fehler beim Export' });
      console.error(error);
    }
  };

  return (
    <>
      <Button variant={variant} onClick={() => setIsOpen(true)}>
        {triggerLabel}
      </Button>
      {isOpen && (
        <Dialog open onClose={() => setIsOpen(false)} maxWidth='sm' fullWidth>
          <DialogTitle>Mandant exportieren</DialogTitle>
          <DialogContent>
            <Typography>
              Wählen Sie aus, in welchem Format der Mandant exportiert werden
              soll.
            </Typography>
            <TextField
              sx={{ mt: 2 }}
              label='Format'
              value={format}
              onChange={e => setFormat(e.target.value as ExportFormatKey)}
              fullWidth
              select
            >
              {FORMAT_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            {format === 'dino' && (
              <Alert severity='info' sx={{ mt: 2 }}>
                <AlertTitle>DiNo-Datensicherung</AlertTitle>
                Verlustfreie Sicherung des Mandanten zum Wiedereinspielen in
                DiNo – etwa bei Übergabe zwischen Installationen oder zur
                Sicherung.
              </Alert>
            )}

            {format === 'strukturdatensatz' && (
              <Alert severity='info' sx={{ mt: 2 }}>
                <AlertTitle>Strukturdatensatz</AlertTitle>
                Standardisiertes Austauschformat der BNotK. Übergibt die
                Stammdaten dieses Mandanten – Name, Anschrift, Geburtsdaten,
                ggf. Firma – an unterstützende Notarsoftware.
              </Alert>
            )}

            {format === 'xjustiz' && (
              <>
                <Alert severity='warning' sx={{ mt: 2 }}>
                  <AlertTitle>Hinweis zum XJustiz-Export</AlertTitle>
                  <Box>
                    XJustiz ist nicht für die Übertragung einzelner Mandanten
                    vorgesehen. Damit gängige Notarprogramme die Stammdaten
                    trotzdem einlesen können, wird der Datensatz als
                    Handelsregister-Mitteilung verpackt. Es handelt sich nicht
                    um einen echten Handelsregister-Auszug.
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    Bei einer <b>natürlichen Person</b> erscheint der Mandant
                    als Gesellschafter einer Container-Firma mit dem Präfix{' '}
                    <b>„NICHT IMPORTIEREN"</b>. Bei einer{' '}
                    <b>juristischen Person</b> wird die Firma normal übergeben.
                    DiNo erkennt den Container beim Re-Import.
                  </Box>
                </Alert>
                {xjustizFormat?.versions &&
                  xjustizFormat.versions.length > 1 && (
                    <TextField
                      sx={{ mt: 2 }}
                      select
                      label='XJustiz-Version'
                      value={
                        xjustizVersion ?? xjustizFormat.defaultVersion ?? ''
                      }
                      onChange={e => setXjustizVersion(e.target.value)}
                      fullWidth
                      helperText='Wählen Sie die Version, die die Empfänger-Software unterstützt. Bei Unsicherheit die Voreinstellung beibehalten.'
                    >
                      {xjustizFormat.versions.map(v => (
                        <MenuItem key={v} value={v}>
                          XJustiz {v}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant='outlined'
              onClick={() => setIsOpen(false)}
              disabled={isExporting}
            >
              Abbruch
            </Button>
            <Button
              variant='contained'
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Wird exportiert …</span>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                'Exportieren'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
